import type {SubscriptionTier} from './subscriptionTier';

export const SESSION_WINDOW_HOURS = 5;
export const DAY_WINDOW_HOURS = 24;

interface SessionWindowLimits {
  session: number;
  day: number;
}

interface SessionWindowSettings {
  sessionLimitPro: number;
  sessionLimitPlus: number;
  dayLimitPro: number;
  dayLimitPlus: number;
}

/** Free tier is capped by credits instead — no session window. */
export function getSessionLimits(
  tier: SubscriptionTier,
  settings: SessionWindowSettings,
): SessionWindowLimits | null {
  switch (tier) {
    case 'free':
      return null;
    case 'plus':
      return {session: settings.sessionLimitPlus, day: settings.dayLimitPlus};
    case 'pro':
      return {session: settings.sessionLimitPro, day: settings.dayLimitPro};
  }
}

function addHours(date: Date, hours: number): Date {
  return new Date(date.getTime() + hours * 3600_000);
}

/**
 * Usage inside the currently running window. A window starts at the first
 * request (windowStart) and lasts a fixed number of hours; once elapsed the
 * next request starts a fresh window (lazy reset — nothing is written until
 * then).
 */
export function resolveWindowUsage(
  windowStart: Date | null,
  requestCount: number,
  windowHours: number,
  now: Date,
): {used: number; resetsAt: Date | null} {
  if (
    windowStart === null ||
    now.getTime() - windowStart.getTime() >= windowHours * 3600_000
  ) {
    return {used: 0, resetsAt: null};
  }
  return {used: requestCount, resetsAt: addHours(windowStart, windowHours)};
}

interface WindowColumns {
  sessionWindowStart: Date | null;
  sessionRequestCount: number;
  dayWindowStart: Date | null;
  dayRequestCount: number;
}

type SessionWindowVerdict =
  | {allowed: true}
  | {allowed: false; limitedBy: 'session' | 'day'; resetsAt: Date};

/**
 * Read-only verdict from the already-committed counters: a window is full once
 * `used >= limit`. The actual increment happens later, when a generation turn
 * is really claimed, so no-op/stuck/lost-race requests are never counted.
 */
export function evaluateSessionWindows(
  row: WindowColumns,
  limits: SessionWindowLimits,
  now: Date,
): SessionWindowVerdict {
  const day = resolveWindowUsage(
    row.dayWindowStart,
    row.dayRequestCount,
    DAY_WINDOW_HOURS,
    now,
  );
  if (day.resetsAt && day.used >= limits.day) {
    return {allowed: false, limitedBy: 'day', resetsAt: day.resetsAt};
  }
  const session = resolveWindowUsage(
    row.sessionWindowStart,
    row.sessionRequestCount,
    SESSION_WINDOW_HOURS,
    now,
  );
  if (session.resetsAt && session.used >= limits.session) {
    return {allowed: false, limitedBy: 'session', resetsAt: session.resetsAt};
  }
  return {allowed: true};
}
